import { aabb, type AABB, type WorldStatics } from '@fisura/sim';
import { applyMapEdits } from './mapEdit.js';
import { MAP_EDITS } from './mapEdits.generated.js';
import { facadePanelBoxes } from './facade.js';

/**
 * "Estación Río de Janeiro" — map v4. A faithful-but-playable greybox of the
 * real Buenos Aires block: Av. Rivadavia between Río de Janeiro st (west) and
 * Av. La Plata (east), Caballito. BF3 Operation-Metro-style vertical layering.
 * Scale: 1 unit = 1 metre (player is 1.8u tall).
 *
 *   z=+85 ┌────────────┬──────┬──────────────────────────┬─────┬───────────┐
 *         │  W block   │ Río  │      NORTH block          │ La  │ POLICE in │
 *         │ (W roof)   │ de   │  [north galería E-W]      │Plata│ RdJ yard  │
 *         │            │Janei.│  (NE roof at the corner)  │     │ (NW)      │
 *   z=+9  ├────────────┴──────┴──────────────────────────┴─────┴───────────┤
 *         │ ════════ AV. RIVADAVIA (median, cars, kiosks) ═══════════════  │
 *         │  ▓ 4 railed subte stairwells ▓ — SUBTE below: 2 tracks + hall  │
 *   z=-9  ├────────────┬──────┬──────────────────────────┬─────┬───────────┤
 *         │  W block   │ Río  │      SOUTH block          │ La  │ FISURA in │
 *         │            │ de   │  [south galería E-W]      │Plata│ LP yard   │
 *         │            │Janei.│  (SW roof at the corner)  │     │ (SE)      │
 *   z=-85 └────────────┴──────┴──────────────────────────┴─────┴───────────┘
 *        x=-92      -78    -64                          64    78        x=92
 *
 * Routes: street (cover-hop), subte (CQC below), galerías (interior),
 * rooftops/terrace (overwatch, exterior stairs). Spawns are screened
 * courtyards (dust2 rule: no sightline reaches a spawn).
 */
export interface MapBox {
  cx: number;
  cz: number;
  sx: number;
  sz: number;
  h: number;
  /** Base height the box sits at (0 = street level). */
  y0?: number;
  color: [number, number, number];
  label?: string;
}

/** Outer extents (kept name for compatibility: halfWidth = x, south/north = z). */
export const AVENUE = {
  halfWidth: 92,
  south: -85,
  north: 85,
} as const;

/** The subte level: track floor (also the global "fall floor"). */
export const TUNNEL_FLOOR = -4.8;
const SLAB = 0.5; // street slab thickness (street top is y = 0)
const BH = 12; // standard building mass height
const ROOF = 5.7; // walkable rooftop base (top of slab = 6.0)

/**
 * THE PALETTE. The whole game draws from one strict, limited set: a cool
 * grey-lavender monochrome ramp (light → dark) plus a single warm accent and
 * the sky tone. No green, no yellow, no red-brown — every material in the map
 * + client resolves to one of these, so the city reads as a unified piece of
 * concept art rather than a candy box. (Reference: the near-monochrome street
 * screenshot — pale ground, grey-lavender blocks, one orange accent.)
 */
export const PALETTE = {
  white: [0.9, 0.9, 0.93] as [number, number, number], // brightest: paint, ground highlight
  light: [0.78, 0.79, 0.85] as [number, number, number], // pale lavender
  mid: [0.6, 0.61, 0.69] as [number, number, number], // grey-lavender
  slate: [0.44, 0.45, 0.54] as [number, number, number], // mid-dark slate
  dark: [0.27, 0.28, 0.36] as [number, number, number], // dark slate / tunnel
  ink: [0.14, 0.14, 0.18] as [number, number, number], // near-black details
  accent: [0.8, 0.5, 0.26] as [number, number, number], // the ONE warm accent
  sky: [0.56, 0.57, 0.63] as [number, number, number], // grey-mauve sky / fog
};

const COLORS = {
  building: PALETTE.slate,
  concrete: PALETTE.mid,
  barricade: PALETTE.accent,
  kiosk: PALETTE.slate,
  walkway: PALETTE.light,
  street: PALETTE.slate,
  tunnel: PALETTE.dark,
  platform: PALETTE.mid,
  rail: PALETTE.ink,
  green: PALETTE.slate, // planters/medians read as concrete, never green
};

/**
 * Plain wall tints for building masses (the client matches these on facades).
 * All one cool lavender family, varying only in lightness (light → slate) so
 * blocks read with depth without ever drifting to green / yellow / brown.
 */
export const WALL_COLORS: [number, number, number][] = [
  [0.8, 0.81, 0.87], // pale lavender (lightest)
  [0.7, 0.71, 0.78],
  [0.61, 0.62, 0.7],
  [0.52, 0.53, 0.62],
  [0.66, 0.66, 0.72], // neutral grey-lavender
  [0.45, 0.46, 0.55], // darkest slate
];

/** A building mass (gets a plain wall tint) vs a labelled structural piece. */
function isBuildingMass(label: string | undefined): boolean {
  return label === undefined || label === 'building';
}

/** Stable per-mass wall tint from position, so adjacent blocks differ. */
function wallColorFor(cx: number, cz: number, h: number): [number, number, number] {
  const k = (Math.round(cx) * 73856093) ^ (Math.round(cz) * 19349663) ^ (Math.round(h) * 83492791);
  return WALL_COLORS[(k >>> 0) % WALL_COLORS.length]!;
}

// Subte stairwell holes in the street (over the OUTER HALF of each track
// tube, leaving a bypass passage beside the staircase below so the stairs
// never plug the tunnel). top = the x end where the stairs meet the street.
const HOLES = [
  { x0: -57.5, x1: -46.5, z0: 6.2, z1: 8.2, top: 'W' as const, name: 'N1' },
  { x0: 6.5, x1: 17.5, z0: 6.2, z1: 8.2, top: 'E' as const, name: 'N2' },
  { x0: -25.5, x1: -14.5, z0: -8.2, z1: -6.2, top: 'E' as const, name: 'S1' },
  { x0: 38.5, x1: 49.5, z0: -8.2, z1: -6.2, top: 'E' as const, name: 'S2' },
];

function buildBoxes(): MapBox[] {
  const boxes: MapBox[] = [];
  const box = (
    cx: number,
    cz: number,
    sx: number,
    sz: number,
    h: number,
    color: [number, number, number],
    y0 = 0,
    label?: string,
  ): void => {
    boxes.push(label ? { cx, cz, sx, sz, h, y0, color, label } : { cx, cz, sx, sz, h, y0, color });
  };
  /** Box from min/max corners (easier for partitions). */
  const span = (
    x0: number,
    x1: number,
    z0: number,
    z1: number,
    h: number,
    color: [number, number, number],
    y0 = 0,
    label?: string,
  ): void => {
    if (x1 - x0 < 0.05 || z1 - z0 < 0.05) return;
    box((x0 + x1) / 2, (z0 + z1) / 2, x1 - x0, z1 - z0, h, color, y0, label);
  };

  // ======================= STREET FLOOR SLABS ==============================
  // The whole playable surface minus the 4 subte stairwell holes.
  const xEdges = (zs: number, ze: number): void => {
    const holes = HOLES.filter((hh) => hh.z0 === zs && hh.z1 === ze);
    let x = -92;
    for (const hh of [...holes].sort((a, b) => a.x0 - b.x0)) {
      span(x, hh.x0, zs, ze, SLAB, COLORS.street, -SLAB, 'street');
      x = hh.x1;
    }
    span(x, 92, zs, ze, SLAB, COLORS.street, -SLAB, 'street');
  };
  span(-92, 92, -85, -8.2, SLAB, COLORS.street, -SLAB, 'street');
  xEdges(-8.2, -6.2);
  span(-92, 92, -6.2, 6.2, SLAB, COLORS.street, -SLAB, 'street');
  xEdges(6.2, 8.2);
  span(-92, 92, 8.2, 85, SLAB, COLORS.street, -SLAB, 'street');

  // ======================= SUBTE (Línea A) =================================
  const uH = -SLAB - TUNNEL_FLOOR; // wall height: floor → slab underside
  const uWall = (x0: number, x1: number, z0: number, z1: number): void =>
    span(x0, x1, z0, z1, uH, COLORS.tunnel, TUNNEL_FLOOR, 'subte-wall');

  uWall(-78, 78, 8.2, 9); // north outer wall
  uWall(-78, 78, -9, -8.2); // south outer wall
  uWall(-78, -77.2, -9, 9); // west end cap
  uWall(77.2, 78, -9, 9); // east end cap

  // Estación Río de Janeiro, Línea A layout: a side PLATFORM against each outer
  // wall, TWO tracks down the centre, split by a row of columns. The stairwells
  // (z±6.2..8.2) land on the platforms. The "A FLORES" train parks on the north
  // track (built as colliders below).
  const PLATFORM_TOP = TUNNEL_FLOOR + 0.9; // -3.9
  span(-72, 72, 3.5, 8.2, 0.9, COLORS.platform, TUNNEL_FLOOR, 'platform'); // north platform
  span(-72, 72, -8.2, -3.5, 0.9, COLORS.platform, TUNNEL_FLOOR, 'platform'); // south platform
  // Centre columns between the two tracks (full height, floor → ceiling).
  for (let x = -66; x <= 66; x += 8) {
    span(x - 0.5, x + 0.5, -0.6, 0.6, uH, COLORS.tunnel, TUNNEL_FLOOR, 'column');
  }
  // Cross steps platform↔track at the hall ends (clear of the train x[-30,30]),
  // two 0.3 risers bridging the 0.9 between platform (-3.9) and track (-4.8) so
  // players can climb back up. One pair on each platform's inner edge.
  const crossStep = (x0: number, x1: number, sz: 1 | -1): void => {
    const e = sz * 3.5; // platform inner edge
    span(x0, x1, Math.min(e, e - sz * 0.3), Math.max(e, e - sz * 0.3), 0.6, COLORS.platform, TUNNEL_FLOOR, 'pstep'); // top -4.2
    span(x0, x1, Math.min(e - sz * 0.3, e - sz * 0.6), Math.max(e - sz * 0.3, e - sz * 0.6), 0.3, COLORS.platform, TUNNEL_FLOOR, 'pstep'); // top -4.5
  };
  for (const sz of [1, -1] as const) {
    crossStep(36, 40, sz);
    crossStep(-40, -36, sz);
  }

  // ── "A FLORES" train, parked on the north track (z 0.7..3.5). Walkable: the
  //    floor is at platform level for level boarding and the platform-side wall
  //    has door gaps. The client (train.ts) draws the livery/interior; these are
  //    just the colliders (not rendered — see world.ts).
  const TX0 = -30;
  const TX1 = 30;
  const TzF = 0.7; // far (centre/track) side
  const TzN = 3.5; // near (platform) side
  const TRH = 2.2; // interior height: floor -3.9 → roof underside -1.7
  span(TX0, TX1, TzF, TzN, 0.9, COLORS.platform, TUNNEL_FLOOR, 'train-floor'); // walkable floor (top -3.9)
  span(TX0, TX1, TzF, TzN, 0.25, COLORS.tunnel, PLATFORM_TOP + TRH, 'train-roof');
  span(TX0, TX1, TzF, TzF + 0.18, TRH, COLORS.tunnel, PLATFORM_TOP, 'train-wall'); // far wall
  span(TX0, TX0 + 0.18, TzF, TzN, TRH, COLORS.tunnel, PLATFORM_TOP, 'train-wall'); // west end
  span(TX1 - 0.18, TX1, TzF, TzN, TRH, COLORS.tunnel, PLATFORM_TOP, 'train-wall'); // east end
  // Platform-side wall, split into segments around the door openings.
  let wx = TX0;
  for (const dc of [-24, -12, 0, 12, 24]) {
    span(wx, dc - 0.9, TzN - 0.18, TzN, TRH, COLORS.tunnel, PLATFORM_TOP, 'train-wall');
    wx = dc + 0.9;
  }
  span(wx, TX1, TzN - 0.18, TzN, TRH, COLORS.tunnel, PLATFORM_TOP, 'train-wall');

  // Stairwells: 16 steps (0.3 rise, 0.55 run) descending from street into the
  // track tube, plus street-level RAILINGS so the hole is visible and safe.
  for (const hh of HOLES) {
    const w = hh.z1 - hh.z0;
    for (let i = 0; i < 16; i++) {
      const top = -0.3 * (i + 1);
      // Each step is a thin tread resting on the step below (front face = one
      // 0.3 riser), NOT a solid pillar to the floor — otherwise the lowest
      // exposed step shows its full floor-to-tread face and reads as a giant
      // first step. Tread heights + positions are unchanged (walkable as before).
      const y0 = Math.max(top - 0.3, PLATFORM_TOP); // stairs land on the platform, not the track
      const h = top - y0;
      if (h < 0.05) continue; // skip the degenerate landing step
      const off = 0.55 * i + 0.275;
      const cx = hh.top === 'W' ? hh.x0 + off : hh.x1 - off;
      box(cx, (hh.z0 + hh.z1) / 2, 0.55, w, h, COLORS.tunnel, y0, 'sstep');
    }
    // Railings: both long sides + the deep-end rim; entry end stays open.
    // (Flush with the hole — no overhang for players/bots to wedge against.)
    span(hh.x0, hh.x1, hh.z1, hh.z1 + 0.3, 1, COLORS.rail, 0, 'srail');
    span(hh.x0, hh.x1, hh.z0 - 0.3, hh.z0, 1, COLORS.rail, 0, 'srail');
    if (hh.top === 'W') span(hh.x1, hh.x1 + 0.3, hh.z0, hh.z1, 1, COLORS.rail, 0, 'srail');
    else span(hh.x0 - 0.3, hh.x0, hh.z0, hh.z1, 1, COLORS.rail, 0, 'srail');
    // SUBTE totem at the entry corner.
    const tx = hh.top === 'W' ? hh.x0 - 1 : hh.x1 + 1;
    box(tx, hh.z1 + 1, 0.6, 0.6, 2.6, COLORS.rail, 0, 'totem');
  }

  // ======================= NORTH-CENTER BLOCK ==============================
  // x -64..64, z 14..80. North galería z 27.5..32.5 (E-W through), two door
  // connectors to the Rivadavia sidewalk, NE roof over the La Plata corner.
  const GN = { z0: 27.5, z1: 32.5 };
  // South strip (z 14..27.5) with door connectors at x=-15 and x=35.
  span(-64, -17.5, 14, GN.z0, BH, COLORS.building);
  span(-12.5, 35, 14, GN.z0, BH, COLORS.building);
  span(-17.5, -12.5, 14, GN.z0, 8.8, COLORS.building, 3.2); // door ceiling
  span(35, 40, 14, GN.z0, 8.8, COLORS.building, 3.2); // door ceiling
  span(40, 62, 14, GN.z0, ROOF, COLORS.building); // under NE roof
  span(62, 64, 14, GN.z0, BH, COLORS.building);
  // Galería ceiling band (z 27.5..32.5).
  span(-64, 40, GN.z0, GN.z1, 8.8, COLORS.building, 3.2);
  span(40, 62, GN.z0, GN.z1, ROOF - 3.2, COLORS.building, 3.2); // under roof
  span(62, 64, GN.z0, GN.z1, 8.8, COLORS.building, 3.2);
  // NE ROOF (overlooks the La Plata junction). Slab + parapets with windows.
  span(40, 62, 14, GN.z1, 0.3, COLORS.walkway, ROOF, 'roof-ne');
  span(40, 46, 13.8, 14.1, 0.9, COLORS.concrete, 6, 'parapet');
  span(51, 62, 13.8, 14.1, 0.9, COLORS.concrete, 6, 'parapet');
  span(61.9, 62.2, 14, 24, 0.9, COLORS.concrete, 6, 'parapet');
  // Exterior stair on the sidewalk along the south face (ascend eastward).
  for (let i = 0; i < 19; i++) {
    box(40.5 + 0.55 * i, 12.75, 0.55, 2.5, 0.3 * (i + 1), COLORS.concrete, 0, 'roofstair');
  }
  // North mass behind the galería, with the police-yard side corridor:
  // V1 (yard → east) x -64..-54, z 64..69; V2 (south to galería) x -59..-54.
  span(-64, -59, GN.z1, 64, BH, COLORS.building);
  span(-54, 64, GN.z1, 64, BH, COLORS.building);
  span(-59, -54, GN.z1, 64, 8.8, COLORS.building, 3.2); // V2 ceiling
  span(-54, 64, 64, 69, BH, COLORS.building);
  span(-64, -54, 64, 69, 8.8, COLORS.building, 3.2); // V1 ceiling
  span(-64, 64, 69, 80, BH, COLORS.building);

  // ======================= SOUTH-CENTER BLOCK ==============================
  // x -64..64, z -80..-14. South galería z -36.5..-31.5 (E-W through), doors
  // at x=-30 and x=10, SW roof over the Río de Janeiro corner.
  const GS = { z0: -36.5, z1: -31.5 };
  // North strip (z -31.5..-14).
  span(-64, -40, GS.z1, -14, ROOF, COLORS.building); // under SW roof
  span(-40, -32.5, GS.z1, -14, BH, COLORS.building);
  span(-32.5, -27.5, GS.z1, -14, 8.8, COLORS.building, 3.2); // door ceiling
  span(-27.5, 7.5, GS.z1, -14, BH, COLORS.building);
  span(7.5, 12.5, GS.z1, -14, 8.8, COLORS.building, 3.2); // door ceiling
  span(12.5, 64, GS.z1, -14, BH, COLORS.building);
  // Galería ceiling band.
  span(-64, -40, GS.z0, GS.z1, ROOF - 3.2, COLORS.building, 3.2); // under roof
  span(-40, 64, GS.z0, GS.z1, 8.8, COLORS.building, 3.2);
  // SW ROOF (overlooks Rivadavia + Río de Janeiro).
  span(-64, -40, GS.z0, -14, 0.3, COLORS.walkway, ROOF, 'roof-sw');
  span(-64, -52, -14.1, -13.8, 0.9, COLORS.concrete, 6, 'parapet');
  span(-47, -40, -14.1, -13.8, 0.9, COLORS.concrete, 6, 'parapet');
  span(-64.2, -63.9, -26, -14, 0.9, COLORS.concrete, 6, 'parapet');
  // Exterior stair along the north face (ascend westward).
  for (let i = 0; i < 19; i++) {
    box(-40.5 - 0.55 * i, -12.75, 0.55, 2.5, 0.3 * (i + 1), COLORS.concrete, 0, 'roofstair');
  }
  // South mass with the fisura-yard side corridor (mirror of the north one):
  // V1' x 54..64, z -69..-64; V2' x 54..59, z -64..-36.5.
  span(-64, 64, -80, -69, BH, COLORS.building);
  span(-64, 54, -69, -64, BH, COLORS.building);
  span(54, 64, -69, -64, 8.8, COLORS.building, 3.2); // V1' ceiling
  span(-64, 54, -64, GS.z0, BH, COLORS.building);
  span(54, 59, -64, GS.z0, 8.8, COLORS.building, 3.2); // V2' ceiling
  span(59, 64, -64, GS.z0, BH, COLORS.building);

  // ======================= WEST EDGE BLOCK =================================
  // x -92..-78. W roof zone z 24..42 (overlooks Río de Janeiro).
  span(-92, -78, -85, 24, BH, COLORS.building);
  span(-92, -78, 24, 42, ROOF, COLORS.building);
  span(-92, -78, 24, 42, 0.3, COLORS.walkway, ROOF, 'roof-w');
  span(-78.2, -77.9, 24, 31, 0.9, COLORS.concrete, 6, 'parapet');
  span(-78.2, -77.9, 36, 42, 0.9, COLORS.concrete, 6, 'parapet');
  span(-92, -78, 42, 85, BH, COLORS.building);
  // Exterior stair in the street along the east face (ascend northward).
  for (let i = 0; i < 19; i++) {
    box(-76.75, 24.5 + 0.55 * i, 2.5, 0.55, 0.3 * (i + 1), COLORS.concrete, 0, 'roofstair');
  }

  // ======================= EAST EDGE BLOCK =================================
  // x 78..92. Plazoleta park (open) z 18..44, terrace y=4 at z 8..18.
  span(78, 92, -85, -62, BH, COLORS.building);
  span(78, 92, -62, 8, BH, COLORS.building);
  span(78, 92, 8, 18, 3.7, COLORS.building);
  span(78, 92, 8, 18, 0.3, COLORS.walkway, 3.7, 'terrace');
  span(78.0, 78.3, 8, 18, 0.9, COLORS.concrete, 4, 'parapet'); // west rim
  span(78, 86, 7.9, 8.2, 0.9, COLORS.concrete, 4, 'parapet'); // south rim
  // Plazoleta: planters + tree trunks (light cover, open ground).
  for (const [px, pz] of [
    [82, 24],
    [88, 30],
    [83, 37],
  ] as [number, number][]) {
    box(px, pz, 2.2, 2.2, 1, COLORS.green, 0, 'planter');
    box(px, pz, 0.6, 0.6, 4, COLORS.building, 1, 'tree');
  }
  span(78, 92, 44, 85, BH, COLORS.building);
  // Terrace stair from the park (ascend southward along the west face).
  for (let i = 0; i < 13; i++) {
    box(79.75, 25.2 - 0.55 * i, 3.5, 0.55, 0.3 * (i + 1), COLORS.concrete, 0, 'roofstair');
  }

  // ======================= SPAWN COURTYARDS ================================
  // Police: north end of Río de Janeiro (x -78..-64). Fisura: south end of
  // Av. La Plata (x 64..78). Screen wall + door + interior blocker each.
  const yard = (
    x0: number,
    x1: number,
    zWall: number,
    doorX: number,
    inside: 1 | -1,
  ): void => {
    // Low riot-fence barriers (see-through; rendered thin by the client). Half
    // height (2 vs the old 4). Collider footprint kept 1u thick / blocker in
    // place so the exit lanes + spawn screening still work; the client draws a
    // thin see-through fence within it.
    const FH = 2;
    span(x0, doorX - 2, zWall - 0.5, zWall + 0.5, FH, COLORS.building, 0, 'spawn-screen');
    span(doorX + 2, x1, zWall - 0.5, zWall + 0.5, FH, COLORS.building, 0, 'spawn-screen');
    span(doorX - 3.5, doorX + 3.5, zWall + inside * 2, zWall + inside * 3, FH, COLORS.building, 0, 'spawn-blocker');
  };
  yard(-78, -64, 58, -71, 1); // police (yard z 58..80)
  yard(64, 78, -58, 71, -1); // fisura (yard z -80..-58)

  // ======================= RIVADAVIA STREET COVER ==========================
  // Median planters — positioned so the bot lane's z-crossings (x ≈ ±40,
  // ±20, 0) all land in the gaps between them.
  for (const x of [-50, -30, -10, 10, 30, 50]) {
    box(x, 0, 6, 2.4, 1.1, COLORS.green, 0, 'median');
  }
  // Parked cars along both curbs (clear of the stairwell holes + the x±61
  // crosswalks). Sized to the player: 4.8 long, 2.0 wide, 1.5 tall.
  for (const x of [-8, 26, 44]) box(x, 7.5, 4.8, 2.0, 1.5, COLORS.kiosk, 0, 'car');
  for (const x of [-50, -34, -4, 10, 28]) box(x, -7.5, 4.8, 2.0, 1.5, COLORS.kiosk, 0, 'car');
  // Solid bus body (north curb) — a collider so players can't stand inside it
  // and shoot out unseen. Clear of the stairwell holes + crosswalks.
  box(-30, 7.5, 12, 2.6, 3.0, COLORS.kiosk, 0, 'bus');
  // Bus shelters (north sidewalk) + news kiosks (south sidewalk).
  for (const x of [-34, 28]) box(x, 10.75, 6, 2.5, 2.4, COLORS.concrete, 0, 'shelter');
  for (const x of [-20, 2, 40]) box(x, -11.5, 3, 2.6, 2.5, COLORS.kiosk, 0, 'kiosk'); // -44 moved to -20 (cleared the roof stair)
  // Tree-trunk colliders (mirror the street.ts foliage grid so players can't
  // walk through trunks). Skip the kiosk/shelter spots. Not rendered (the
  // client draws the trees) — see world.ts.
  const treeBlk: [number, number][] = [[-20, -11.5], [2, -11.5], [40, -11.5], [-34, 10.75], [28, 10.75]];
  for (const sgn of [1, -1]) {
    for (let x = -56; x <= 56; x += 14) {
      const z = sgn * 10;
      if (treeBlk.some(([bx, bz]) => Math.abs(x - bx) < 5 && Math.sign(bz) === sgn)) continue;
      box(x, z, 0.6, 0.6, 3, COLORS.kiosk, 0, 'tree');
    }
  }

  // ======================= PERIMETER (visual) ==============================
  for (let x = -88; x < 92; x += 24) {
    box(x + 10, 90, 22, 8, 16, COLORS.building);
    box(x + 10, -90, 22, 8, 16, COLORS.building);
  }
  for (let z = -80; z < 85; z += 24) {
    box(-97, z + 10, 8, 22, 16, COLORS.building);
    box(97, z + 10, 8, 22, 16, COLORS.building);
  }

  return boxes;
}

/**
 * The hand-authored map, with building-mass tints resolved into `color` up
 * front (so when the editor carves a building into fragments, every piece keeps
 * the same tint instead of re-hashing to a random colour).
 */
export const BASE_BOXES: MapBox[] = [
  ...buildBoxes().map((b) =>
    isBuildingMass(b.label) ? { ...b, color: wallColorFor(b.cx, b.cz, b.h) } : b,
  ),
  // Windowed facade panels — carvable like everything else, but VISUAL only
  // (filtered out of buildStatics; the mass behind provides collision).
  ...facadePanelBoxes(),
];

/** Base map + saved editor edits (carved tunnels, placed blocks, stairs). */
export const MAP_BOXES: MapBox[] = applyMapEdits(BASE_BOXES, MAP_EDITS);

// The two spawn yards sit at opposite corners of the map. Face each team OUT of
// its yard toward the enemy yard on (re)spawn — yaw 0/π pointed them at their own
// back wall. Forward is (sin yaw, cos yaw), so yaw = atan2(dx, dz) toward a point.
const FISURA_YARD = { x: 71, z: -70 }; // Av. La Plata (east/south)
const POLICE_YARD = { x: -71, z: 70 }; // Río de Janeiro (west/north)
const faceYaw = (from: { x: number; z: number }, to: { x: number; z: number }): number =>
  Math.atan2(to.x - from.x, to.z - from.z);
const FISURA_FACE = faceYaw(FISURA_YARD, POLICE_YARD); // look toward the Police yard
const POLICE_FACE = faceYaw(POLICE_YARD, FISURA_YARD); // look toward the Fisura yard

/** Legacy single spawn (compatibility; prefer TEAM_SPAWN_POINTS). */
export const TEAM_SPAWN: Record<number, { x: number; z: number; yaw: number }> = {
  0: { x: 71, z: -70, yaw: FISURA_FACE }, // Fisura: Av. La Plata south yard
  1: { x: -71, z: 70, yaw: POLICE_FACE }, // Police: Río de Janeiro north yard
};

/** Spawn candidates — all inside the screened courtyards, facing the enemy yard. */
export const TEAM_SPAWN_POINTS: Record<number, { x: number; z: number; yaw: number }[]> = {
  0: [
    { x: 71, z: -77, yaw: FISURA_FACE },
    { x: 67, z: -73, yaw: FISURA_FACE },
    { x: 75, z: -73, yaw: FISURA_FACE },
    { x: 67, z: -66, yaw: FISURA_FACE },
    { x: 75, z: -66, yaw: FISURA_FACE },
  ],
  1: [
    { x: -71, z: 77, yaw: POLICE_FACE },
    { x: -75, z: 73, yaw: POLICE_FACE },
    { x: -67, z: 73, yaw: POLICE_FACE },
    { x: -75, z: 66, yaw: POLICE_FACE },
    { x: -67, z: 66, yaw: POLICE_FACE },
  ],
};

/**
 * Each team's spawn yard as an invisible keep-out volume. A player only ever
 * gets the ENEMY team's box added to their colliders (see buildStaticsForTeam),
 * so you move freely in your own yard but hit a solid wall at the fence line of
 * the enemy's — no spawn-raiding. Tall (y −5..30) so it can't be jumped or
 * dropped into; aligned to the screen-fence faces (z ∓58) so it reads naturally.
 */
export const SPAWN_ZONE: Record<number, AABB> = {
  0: aabb(71, -69, 7, 11, -5, 30), // Fisura yard: x 64..78, z -80..-58
  1: aabb(-71, 69, 7, 11, -5, 30), // Police yard: x -78..-64, z 58..80
};

// Shared yard exit/entry waypoints. The path goes through the door, then
// slides along the 1.5u corridor between screen wall and blocker (S-curve).
const FISURA_EXIT = [
  { x: 76.3, z: -63 },
  { x: 76.3, z: -59.3 },
  { x: 71, z: -59.3 },
  { x: 71, z: -54 },
];
const POLICE_ENTRY = [
  { x: -71, z: 54 },
  { x: -71, z: 59.3 },
  { x: -76.3, z: 59.3 },
  { x: -76.3, z: 63 },
];

/**
 * Bot lanes, ordered Fisura → Police (team 1 walks them reversed):
 * - center: Av. La Plata → cover-hop west along Rivadavia → Río de Janeiro.
 * - west:   the SUBTE — down stairwell S2, west along the south tube's
 *           bypass, cross at the hall's west steps, out stairwell N1.
 * - east:   the south GALERÍA straight through the block.
 */
export const LANES: Record<'west' | 'center' | 'east', { x: number; z: number }[]> = {
  center: [
    ...FISURA_EXIT,
    { x: 71, z: -26 },
    { x: 58, z: -3 },
    { x: 50, z: -3 },
    { x: 30, z: 3 },
    { x: 10, z: -3 },
    { x: -10, z: 3 },
    { x: -30, z: -3 },
    { x: -50, z: 3 },
    { x: -66, z: 0 },
    { x: -71, z: 26 },
    ...POLICE_ENTRY,
  ],
  west: [
    ...FISURA_EXIT,
    { x: 71, z: -26 },
    { x: 56, z: -7.2 }, // approach S2 from the east
    { x: 50, z: -7.2 }, // S2 stair top (descends westward onto the south platform)
    { x: 42, z: -7.2 }, // on the south platform (stair band)
    { x: 42, z: -5 }, // move to the inner platform, clear of the stairwells
    { x: 0, z: -5 }, // walk west along the south platform
    { x: -44, z: -5 }, // past the parked train, toward the west crossing
    { x: -38, z: -6.5 }, // fully on the south platform (forces the climb in reverse)
    { x: -38, z: -1 }, // down the cross-steps onto the south track
    { x: -38, z: 1 }, // cross the centre (gap between columns)
    { x: -38, z: 6.5 }, // climb the cross-steps fully onto the north platform
    { x: -47.5, z: 7.2 }, // N1 stair base on the platform
    { x: -57, z: 7.2 }, // climb N1 westward to the street
    { x: -66, z: 4 },
    { x: -71, z: 26 },
    ...POLICE_ENTRY,
  ],
  east: [
    ...FISURA_EXIT,
    { x: 71, z: -34 },
    { x: 62, z: -34 }, // south galería east mouth
    { x: 30, z: -34 },
    { x: 0, z: -34 },
    { x: -30, z: -34 },
    { x: -60, z: -34 }, // west mouth
    { x: -71, z: -20 },
    { x: -71, z: 26 },
    ...POLICE_ENTRY,
  ],
};

/** Build the simulation statics (colliders + bounds) from the map data. */
export function buildStatics(boxes: MapBox[] = MAP_BOXES): WorldStatics {
  return {
    // Facade panels are visual-only (the building mass behind is the collider).
    colliders: boxes
      .filter((b) => !b.label?.startsWith('facade-'))
      .map((b) =>
      aabb(b.cx, b.cz, b.sx / 2, b.sz / 2, b.y0 ?? 0, (b.y0 ?? 0) + b.h),
    ),
    bounds: {
      minX: -AVENUE.halfWidth + 0.5,
      maxX: AVENUE.halfWidth - 0.5,
      minZ: AVENUE.south + 1,
      maxZ: AVENUE.north - 1,
    },
    groundY: TUNNEL_FLOOR,
  };
}

/**
 * Statics for a specific team: the base world plus the OPPOSING team's spawn
 * zone as a keep-out collider. Pass the mover's team so each player is walled
 * out of the enemy spawn while their own stays open. teamId other than 0/1
 * falls back to the plain world.
 */
export function buildStaticsForTeam(teamId: number, boxes: MapBox[] = MAP_BOXES): WorldStatics {
  const base = buildStatics(boxes);
  const enemyZone = SPAWN_ZONE[teamId === 0 ? 1 : teamId === 1 ? 0 : -1];
  if (!enemyZone) return base;
  return { ...base, colliders: [...base.colliders, enemyZone] };
}
